import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Send, Loader2, Bot, User, Sparkles, Target, Users, 
  TrendingUp, BookOpen, Zap, Video, CheckCircle, Shirt,
  AlertCircle
} from 'lucide-react';
import {
  supabase,
  uploadVideoToGCS,
  detectJerseys,
  analyzeTeam,
  preflightCheck
} from '../services/api';
import axios from 'axios';

// ✅ Cloud Run API for AI agents
const CLOUD_RUN_API = process.env.REACT_APP_FLASK_API_URL || 'https://tahleel-api-796940649294.us-central1.run.app';

/**
 * ChatInterface Component
 * 
 * Features:
 * - AI tactical chat (formations, training, analysis)
 * - INLINE video upload with jersey detection
 * - Coach selects team by jersey color IN THE CHAT
 * - Full analysis results appear as chat messages
 */
const AGENT_DESCRIPTIONS = {
  tactical:  'tactical analysis — match breakdowns, weaknesses, and counter-tactics',
  formation: 'formation detection — identifying and countering opponent formations',
  movement:  'player movement — heat maps and positional analysis',
  training:  'training plans — drills, schedules, and session planning',
  injury:    'movement risk flags — detecting fatigue, asymmetry, and biomechanical anomalies for medical staff review',
};

const TRAINING_SCENARIOS = [
  { value: '', label: 'No Scenario (General Plan)' },
  { value: 'winning_1_0', label: 'Winning 1-0 — Protecting a Lead' },
  { value: 'losing_0_1', label: 'Losing 0-1 — Chasing the Game' },
  { value: 'winning_2_0', label: 'Winning 2-0 — Game Management' },
  { value: 'losing_0_2', label: 'Losing 0-2 — High Risk Attack' },
  { value: 'high_press', label: 'High Press Execution' },
  { value: 'low_block', label: 'Low Block Defence' },
  { value: 'set_piece_focus', label: 'Set Piece Focus Session' },
  { value: 'counter_attack', label: 'Counter-Attack Transitions' },
];

const ChatInterface = ({ initialPrompt, onPromptUsed, activeAgent }) => {
  const { t, i18n } = useTranslation();
  const prevAgentRef = useRef(null);

  // Chat state
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Welcome to TAHLEEL.ai - AI-Powered Football Tactical Analysis.\n\nI'm your AI tactical coach. Upload match videos for deep analysis or ask about formations, tactics, and training strategies.`,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressMessage, setUploadProgressMessage] = useState('');
  
  // Jersey selection state (for inline flow)
  const [pendingJerseySelection, setPendingJerseySelection] = useState(null);
  const [analyzingTeam, setAnalyzingTeam] = useState(false);

  // Preflight confirmation state
  const [pendingPreflightConfirm, setPendingPreflightConfirm] = useState(null);
  const preflightTimerRef = useRef(null);

  // Scenario selector state (for training plan customization)
  const [selectedScenario, setSelectedScenario] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle initial prompt from parent
  useEffect(() => {
    if (initialPrompt && initialPrompt.trim()) {
      setInput(initialPrompt);
      inputRef.current?.focus();
      if (onPromptUsed) {
        onPromptUsed();
      }
    }
  }, [initialPrompt, onPromptUsed]);

  // Handle agent switch — add a system message and suggest a relevant prompt
  const AGENT_PROMPTS = {
    tactical:  'Analyze the opponent weaknesses from the last match',
    formation: 'What formation counters a 4-3-3?',
    movement:  'Show player movement patterns and heat map analysis',
    training:  'Generate a 7-day training plan for our next match',
    injury:    'Run movement risk analysis on the last uploaded video',
  };

  useEffect(() => {
    if (activeAgent && activeAgent !== prevAgentRef.current && prevAgentRef.current !== null) {
      const desc = AGENT_DESCRIPTIONS[activeAgent] || activeAgent;
      const agentName = activeAgent.charAt(0).toUpperCase() + activeAgent.slice(1);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Switched to **${agentName}** agent. Ask me anything about ${desc}.`,
        timestamp: new Date()
      }]);
      // Pre-fill input with a relevant suggestion
      const suggestion = AGENT_PROMPTS[activeAgent];
      if (suggestion) {
        setInput(suggestion);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
    prevAgentRef.current = activeAgent;
  }, [activeAgent]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingJerseySelection]);

  /**
   * Detect user intent from message
   */
  const detectIntent = (message) => {
    const msg = message.toLowerCase();

    // Recall / history intent — check first
    const recallKeywords = ['show me', 'recall', 'last analysis', 'previous analysis', 'history',
      'what did we find', 'show analysis', 'my analyses', 'past analysis', 'recent analysis',
      'find analysis', 'look up', 'search analysis'];
    if (recallKeywords.some(k => msg.includes(k))) {
      return { type: 'recall', endpoint: null };
    }

    if (msg.includes('analyze') || msg.includes('weakness') || msg.includes('beat') || msg.includes('play against')) {
      return { type: 'tactical', endpoint: '/api/agents/quick-advice' };
    }
    if (msg.includes('formation') || msg.match(/\d-\d-\d(-\d)?/)) {
      if (msg.includes('compare')) {
        return { type: 'formation-compare', endpoint: '/api/agents/formation/compare' };
      }
      return { type: 'formation', endpoint: '/api/agents/formation/explain' };
    }
    if (msg.includes('training') || msg.includes('drill') || msg.includes('practice')) {
      if (msg.includes('drill')) {
        return { type: 'training-drill', endpoint: '/api/agents/training/drill' };
      }
      return { type: 'training', endpoint: '/api/agents/training' };
    }
    if (msg.includes('player') || msg.includes('movement') || msg.includes('positioning')) {
      return { type: 'player-movement', endpoint: '/api/agents/quick-advice' };
    }
    return { type: 'tactical', endpoint: '/api/agents/quick-advice' };
  };

  /**
   * Handle recall intent — search Supabase for past analyses
   */
  const handleRecall = async (userInput) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        addAssistantMessage('Please log in to view your analysis history.', true);
        return;
      }

      // Extract team name from query if present
      const msg = userInput.toLowerCase();
      const teamMatch = userInput.match(/al\s+\w+/i) || userInput.match(/(?:about|for|of|on)\s+(.+?)(?:\?|$)/i);
      const searchTerm = teamMatch ? (teamMatch[1] || teamMatch[0]).trim() : null;

      let query = supabase
        .from('analyses')
        .select('id, opponent_team, status, confidence_score, processing_time_seconds, created_at, analysis_data')
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      // Search by team name if provided
      if (searchTerm && !msg.includes('last') && !msg.includes('recent') && !msg.includes('history')) {
        query = query.ilike('opponent_team', `%${searchTerm}%`);
      }

      const { data: analyses, error } = await query.limit(5);

      if (error) throw error;

      if (!analyses || analyses.length === 0) {
        addAssistantMessage(
          searchTerm
            ? `No analyses found for "${searchTerm}". Try a different team name or say "show recent analysis".`
            : 'No completed analyses found. Upload a match video to get started.'
        );
        return;
      }

      // Build response
      let response = '';

      if (analyses.length === 1) {
        // Single result — show detail
        const a = analyses[0];
        const ad = a.analysis_data || {};
        const tactical = ad.tactical || {};
        const formation = tactical.inferred_formation || ad.inferredFormation || '';
        const indices = tactical.tactical_indices || ad.tacticalIndices || {};
        const fullAnalysis = ad.full_analysis || tactical.full_analysis || '';

        response = `**Analysis Found: ${a.opponent_team}**\n`;
        response += `Date: ${new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}\n`;
        response += `Confidence: ${a.confidence_score || 0}%\n`;
        if (formation) response += `Formation: **${formation}**\n`;
        if (indices.pressure_index != null) response += `Press Intensity: ${Math.round(indices.pressure_index * 100)}%\n`;
        if (indices.width_index != null) response += `Width Index: ${indices.width_index.toFixed(2)}\n`;

        if (fullAnalysis) {
          // Show first 500 chars of the analysis
          const preview = fullAnalysis.length > 500 ? fullAnalysis.substring(0, 500) + '...' : fullAnalysis;
          response += `\n---\n\n${preview}`;
        }

        response += `\n\n*View full report in Analysis History.*`;

      } else {
        // Multiple results — show list
        response = `**Found ${analyses.length} analyses:**\n\n`;

        analyses.forEach((a, i) => {
          const date = new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const formation = a.analysis_data?.tactical?.inferred_formation || a.analysis_data?.inferredFormation || '';
          response += `**${i + 1}. ${a.opponent_team}** — ${date}`;
          if (a.confidence_score) response += ` (${a.confidence_score}%)`;
          if (formation) response += ` | ${formation}`;
          response += '\n';
        });

        response += `\n*Say "show me the [team name] analysis" for details, or view all in Analysis History.*`;
      }

      addAssistantMessage(response, false, false, 'recall');

    } catch (err) {
      console.error('Recall error:', err);
      addAssistantMessage(`Failed to search analyses: ${err.message}`, true);
    }
  };

  /**
   * ============================================
   * VIDEO UPLOAD WITH INLINE JERSEY DETECTION
   * ============================================
   */
  
  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('video/')) {
      addAssistantMessage('❌ Please upload a valid video file (MP4, MOV, AVI, WEBM)', true);
      return;
    }

    if (file.size > 2000 * 1024 * 1024) {
      addAssistantMessage('❌ Video file too large. Maximum size: 2GB', true);
      return;
    }

    try {
      setUploadingVideo(true);
      setUploadProgress(0);
      setPendingJerseySelection(null);
      setPendingPreflightConfirm(null);

      addAssistantMessage(
        `**Uploading Video:** ${file.name}\n` +
        `Size: ${(file.size / (1024 * 1024)).toFixed(1)} MB\n\n` +
        `Starting upload...`
      );

      // STEP 1: Upload to GCS
      console.log('🎬 [STEP 1] Uploading video...');

      const uploadResult = await uploadVideoToGCS(file, (percent, message) => {
        setUploadProgress(percent);
        setUploadProgressMessage(message);
      });

      console.log('✅ Upload complete:', uploadResult);

      updateLastAssistantMessage(
        `**Video Uploaded:** ${file.name}\n\n` +
        `Upload complete!\n\n` +
        `Analyzing broadcast footage...`
      );

      // STEP 1.5: Vision Preflight — read broadcast info
      console.log('🔍 [STEP 1.5] Running preflight check...');
      setUploadProgressMessage('AI is analyzing sample frames...');

      let preflightResult = null;
      try {
        preflightResult = await preflightCheck(uploadResult.gcsPath, uploadResult.videoId);
        console.log('✅ Preflight result:', preflightResult);
      } catch (pfErr) {
        console.warn('⚠️ Preflight failed, continuing without:', pfErr);
      }

      // Build match info from preflight
      const matchInfo = {};
      if (preflightResult?.success) {
        const t = preflightResult.teams || {};
        const j = preflightResult.jerseys || {};
        const fa = preflightResult.frameAnalysis || {};

        if (t.team_0_name) matchInfo.team0 = t.team_0_name;
        if (t.team_1_name) matchInfo.team1 = t.team_1_name;
        if (j.team_0_color) matchInfo.jersey0 = `${j.team_0_color} (${j.team_0_hex || ''})`;
        if (j.team_1_color) matchInfo.jersey1 = `${j.team_1_color} (${j.team_1_hex || ''})`;
        if (t.source) matchInfo.source = t.source;
        if (t.confidence) matchInfo.confidence = t.confidence;

        // Frame analysis
        if (preflightResult.frameAnalysis) {
          matchInfo.frameAnalysis = preflightResult.frameAnalysis;
        }
      }

      const hasMatchInfo = matchInfo.team0 || matchInfo.team1 || matchInfo.jersey0;

      if (hasMatchInfo) {
        // BUILD CONFIRMATION MESSAGE with frame analysis
        let confirmMsg = `**AI Broadcast Analysis Complete**\n\n`;
        confirmMsg += `I analyzed sample frames from your video and found:\n\n`;

        if (matchInfo.team0 && matchInfo.team1) {
          confirmMsg += `**Match:** ${matchInfo.team0} vs ${matchInfo.team1}\n`;
        } else if (matchInfo.team0) {
          confirmMsg += `**Team detected:** ${matchInfo.team0}\n`;
        } else if (matchInfo.team1) {
          confirmMsg += `**Team detected:** ${matchInfo.team1}\n`;
        }

        if (matchInfo.jersey0) confirmMsg += `**Team A Jersey:** ${matchInfo.jersey0}\n`;
        if (matchInfo.jersey1) confirmMsg += `**Team B Jersey:** ${matchInfo.jersey1}\n`;
        if (matchInfo.source) confirmMsg += `**Source:** ${matchInfo.source}\n`;
        if (matchInfo.confidence) confirmMsg += `**Confidence:** ${matchInfo.confidence}\n`;

        // Add frame analysis to the message
        const fa = matchInfo.frameAnalysis;
        if (fa && fa.total_sampled > 0) {
          confirmMsg += `\n---\n\n`;
          confirmMsg += `**AI Frame Quality Scan** (${fa.total_sampled} frames sampled)\n\n`;
          confirmMsg += `Tactical frames (usable): **${fa.tactical_frames}**\n`;
          confirmMsg += `Garbage frames (removed): **${fa.garbage_frames}** (${fa.garbage_rate_pct}% waste)\n\n`;

          // Show per-frame breakdown
          const classifications = fa.classifications || [];
          if (classifications.length > 0) {
            classifications.forEach(fc => {
              const icon = fc.usable ? '✅' : '🗑️';
              const ts = fc.timestamp_sec != null
                ? `[${Math.floor(fc.timestamp_sec/60)}:${String(Math.floor(fc.timestamp_sec%60)).padStart(2,'0')}]`
                : '';
              confirmMsg += `${icon} Frame ${fc.frame_number} ${ts} — **${fc.classification}**: ${fc.reason}\n`;
            });
          }

          if (fa.garbage_summary) {
            confirmMsg += `\n*${fa.garbage_summary}*\n`;
          }
        }

        confirmMsg += `\n**Is this the correct match?**\n`;
        confirmMsg += `*Auto-continuing in 2 minutes if no response...*`;

        addAssistantMessage(confirmMsg);

        // Show garbage frame image if available
        if (fa?.garbage_frame_url && !fa.garbage_frame_url.startsWith('gs://')) {
          addAssistantMessage(
            `**Example garbage frame identified by AI:**\n\n![Garbage Frame](${fa.garbage_frame_url})\n\n*This type of frame will be automatically filtered out to save processing time and improve accuracy.*`,
            false, false, 'frame-analysis'
          );
        }

        // PAUSE PIPELINE — wait for coach confirmation or 2-minute timeout
        setPendingPreflightConfirm({
          videoInfo: {
            videoId: uploadResult.videoId,
            gcsPath: uploadResult.gcsPath,
            filename: file.name
          },
          matchInfo,
          preflightResult,
          confirmedAt: null,
        });

        // Start 2-minute auto-continue timer
        if (preflightTimerRef.current) clearTimeout(preflightTimerRef.current);
        preflightTimerRef.current = setTimeout(() => {
          console.log('⏰ Preflight confirmation timed out — auto-continuing');
          handlePreflightConfirm(true);
        }, 120000); // 2 minutes

        // Stop here — pipeline continues in handlePreflightConfirm
        return;

      } else {
        // No broadcast info detected — skip confirmation, go straight to jersey detection
        addAssistantMessage(
          `**Broadcast Analysis:** Could not read match details from video.\n` +
          `Continuing with jersey detection...`
        );
        await continueToJerseyDetection(uploadResult, file.name);
      }

    } catch (error) {
      console.error('❌ Video upload/detection error:', error);
      addAssistantMessage(
        `❌ **Video Processing Failed**\n\n${error.message}\n\nPlease try again with a different video.`,
        true
      );
      setPendingJerseySelection(null);
      setPendingPreflightConfirm(null);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      setUploadProgressMessage('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handle preflight confirmation (coach clicks Yes/No or 2-min timeout)
   */
  const handlePreflightConfirm = async (confirmed) => {
    if (!pendingPreflightConfirm) return;

    // Clear timer
    if (preflightTimerRef.current) {
      clearTimeout(preflightTimerRef.current);
      preflightTimerRef.current = null;
    }

    const { videoInfo, matchInfo } = pendingPreflightConfirm;
    setPendingPreflightConfirm(null);

    if (confirmed) {
      const teamStr = (matchInfo.team0 && matchInfo.team1)
        ? `${matchInfo.team0} vs ${matchInfo.team1}`
        : 'this match';
      addUserMessage(`Yes, continue with ${teamStr}`);
      addAssistantMessage(`Confirmed! Proceeding with jersey detection...`);
    } else {
      addUserMessage('No, this is not the right match');
      addAssistantMessage(`Understood. Proceeding with jersey detection — you can select the correct team manually.`);
    }

    await continueToJerseyDetection(
      { videoId: videoInfo.videoId, gcsPath: videoInfo.gcsPath },
      videoInfo.filename
    );
  };

  /**
   * Continue pipeline: jersey detection → team selection
   */
  const continueToJerseyDetection = async (uploadResult, filename) => {
    try {
      console.log('🎽 [STEP 2] Detecting jerseys...');
      setUploadProgressMessage('Detecting jersey colors...');

      const jerseyResult = await detectJerseys(uploadResult.gcsPath, uploadResult.videoId);

      console.log('✅ Jersey detection result:', jerseyResult);

      if (!jerseyResult.success || !jerseyResult.jerseys || jerseyResult.jerseys.length < 2) {
        throw new Error(jerseyResult.error || 'Could not detect 2 teams. Please upload clearer match footage.');
      }

      addAssistantMessage(
        `Detected **${jerseyResult.jerseys.length} teams** in the video!\n\n` +
        `**Select which team you want to analyze:**`
      );

      setPendingJerseySelection({
        jerseys: jerseyResult.jerseys,
        videoInfo: {
          videoId: uploadResult.videoId,
          gcsPath: uploadResult.gcsPath,
          filename: filename
        }
      });

    } catch (error) {
      console.error('❌ Jersey detection error:', error);
      addAssistantMessage(
        `❌ **Jersey Detection Failed**\n\n${error.message}\n\nPlease try again with different footage.`,
        true
      );
    }
  };

  /**
   * Handle team selection from inline jersey selector
   */
  const handleTeamSelect = async (jersey, customLabel = '') => {
    if (!pendingJerseySelection) return;

    const { videoInfo } = pendingJerseySelection;
    const teamLabel = customLabel.trim() || `${jersey.color_name} Jersey Team`;

    // Clear jersey selector
    setPendingJerseySelection(null);
    setAnalyzingTeam(true);

    // Add user selection message
    addUserMessage(`Analyze the ${jersey.color_name} jersey team`);

    // Show analyzing message
    addAssistantMessage(
      `🎯 **Analyzing ${teamLabel}...**\n\n` +
      `⏳ Running AI tactical analysis on ${jersey.player_count} detected players.\n\n` +
      `This takes 2-4 minutes. Please wait...`,
      false,
      true // typing indicator
    );

    try {
      console.log('🎯 [STEP 3] Analyzing team:', jersey);

      const analysisResult = await analyzeTeam({
        gcsPath: videoInfo.gcsPath,
        videoId: videoInfo.videoId,
        selectedTeamId: jersey.team_id,
        teamLabel: teamLabel,
        teamColor: jersey.hex
      });

      console.log('✅ Analysis result:', analysisResult);

      if (!analysisResult.success) {
        throw new Error(analysisResult.error || 'Analysis failed');
      }

      // Remove typing message and add results
      removeTypingMessages();

      const fullAnalysis = analysisResult.results?.fullAnalysis || 
                          analysisResult.tactical_analysis?.full_analysis ||
                          analysisResult.message ||
                          'Analysis completed successfully!';

      const processingTime = analysisResult.metadata?.processing_time_seconds || 0;
      const framesAnalyzed = analysisResult.detections_summary?.total_frames_analyzed || 0;

      // Pipeline visibility — show what the AI did
      const pipeline = analysisResult.pipeline;
      let pipelineText = '';
      let garbageFrameUrl = null;

      if (pipeline) {
        const ff = pipeline.frame_filter || {};
        const prefl = pipeline.preflight || {};
        const fa = prefl.frame_analysis || {};
        const lines = [];

        if (prefl.ran) {
          const tNames = prefl.teams;
          if (tNames?.team_0_name && tNames?.team_1_name) {
            lines.push(`**Teams Identified:** ${tNames.team_0_name} vs ${tNames.team_1_name}`);
          }
          lines.push(`**Vision Preflight:** Broadcast analyzed — crop & enhance applied`);
        }

        // Task D: AI Frame Classification
        if (fa.total_sampled > 0) {
          lines.push(`\n**AI Frame Analysis** (${fa.total_sampled} samples inspected):`);
          lines.push(`  Tactical frames: **${fa.tactical_frames}** | Garbage frames: **${fa.garbage_frames}** (${fa.garbage_rate_pct}% garbage rate)`);

          // Show per-frame classifications
          const classifications = fa.classifications || [];
          if (classifications.length > 0) {
            const classLines = classifications.map(fc => {
              const icon = fc.usable ? '✅' : '🗑️';
              const ts = fc.timestamp_sec ? `[${Math.floor(fc.timestamp_sec/60)}:${String(Math.floor(fc.timestamp_sec%60)).padStart(2,'0')}]` : '';
              return `  ${icon} Frame ${fc.frame_number} ${ts} — **${fc.classification}**: ${fc.reason}`;
            });
            lines.push(classLines.join('\n'));
          }

          if (fa.garbage_summary) {
            lines.push(`\n*${fa.garbage_summary}*`);
          }

          // Capture garbage frame URL for image display
          if (fa.garbage_frame_url && !fa.garbage_frame_url.startsWith('gs://')) {
            garbageFrameUrl = fa.garbage_frame_url;
          }
        }

        if (ff.total_rejected > 0) {
          lines.push(`\n**Frame Filter Result:** ${ff.total_checked} frames processed — **${ff.total_rejected} removed** (${ff.pass_rate_pct}% pass rate)`);
        }

        if (pipeline.players_detected > 0) {
          lines.push(`**Players Detected:** ${pipeline.players_detected} across ${pipeline.frames_to_yolo || framesAnalyzed} frames`);
        }

        if (lines.length > 0) {
          pipelineText = `\n\n**AI Pipeline:**\n` + lines.join('\n') + '\n';
        }
      }

      // Heatmap URLs
      const heatmaps = analysisResult.heatmaps;
      let heatmapText = '';
      if (heatmaps && (heatmaps.team_0 || heatmaps.team_1)) {
        heatmapText = '\n\n**Heatmaps Generated** — view in the full analysis report.\n';
      }

      addAssistantMessage(
        `**Analysis Complete: ${teamLabel}**\n\n` +
        `**Processing Time:** ${Math.round(processingTime)}s\n` +
        `**Frames Analyzed:** ${framesAnalyzed}\n` +
        `**Jersey Color:** ${jersey.color_name} (${jersey.hex})` +
        pipelineText +
        heatmapText +
        `\n\n---\n\n` +
        `${fullAnalysis}`,
        false,
        false,
        'video-analysis',
        analysisResult.video_id
      );

      // Show garbage frame as a separate image message if available
      if (garbageFrameUrl) {
        addAssistantMessage(
          `**Example Garbage Frame Removed:**\nThe AI identified this frame as non-tactical and filtered it out automatically. Frames like this waste processing time and reduce analysis accuracy.\n\n![Garbage Frame](${garbageFrameUrl})`,
          false,
          false,
          'frame-analysis'
        );
      }

      // Auto-save to Supabase
      await saveAnalysisToSupabase({
        videoInfo,
        jersey,
        teamLabel,
        analysisResult,
        fullAnalysis
      });

    } catch (error) {
      console.error('❌ Team analysis error:', error);
      removeTypingMessages();
      addAssistantMessage(
        `❌ **Analysis Failed**\n\n${error.message}\n\nPlease try uploading the video again.`,
        true
      );
    } finally {
      setAnalyzingTeam(false);
    }
  };

  /**
   * Save analysis to Supabase
   */
  const saveAnalysisToSupabase = async ({ videoInfo, jersey, teamLabel, analysisResult, fullAnalysis }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('⚠️ User not logged in, skipping auto-save');
        return;
      }

      const { data, error } = await supabase
        .from('analyses')
        .insert({
          created_by: user.id,
          opponent_team: teamLabel,
          analysis_type: 'video',
          status: 'completed',
          analysis_data: {
            video_id: videoInfo.videoId,
            full_analysis: fullAnalysis,
            tactical: analysisResult.tactical_analysis,
            pipeline: analysisResult.pipeline,
            heatmaps: analysisResult.heatmaps,
            ...analysisResult.results
          },
          vision_data: {
            video_id: videoInfo.videoId,
            detections: analysisResult.detections_summary,
            metadata: analysisResult.metadata,
            selected_team: {
              team_id: jersey.team_id,
              color: jersey.hex,
              color_name: jersey.color_name,
              player_count: jersey.player_count
            }
          },
          processing_time_seconds: Math.round(analysisResult.metadata?.processing_time_seconds || 0),
          confidence_score: 85,
          ai_enhanced: true,
          data_source: 'Tahleel Vision + TAHLEEL AI Engine'
        })
        .select()
        .single();
      
      if (error) {
        console.error('❌ Auto-save failed:', error);
      } else {
        console.log('✅ Auto-saved to Supabase:', data.id);
        addAssistantMessage(
          `💾 **Analysis saved!**\n\nYou can find it in your Analysis History.\n\n` +
          `💡 **Tip:** Ask me follow-up questions like:\n` +
          `• "What training drills should we run?"\n` +
          `• "What formation counters this team?"\n` +
          `• "Generate a 7-day training plan"`
        );
      }
    } catch (saveError) {
      console.error('❌ Auto-save error:', saveError);
    }
  };

  /**
   * ============================================
   * CHAT MESSAGE HELPERS
   * ============================================
   */

  const addAssistantMessage = (content, isError = false, isTyping = false, agentType = null, analysisId = null) => {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content,
      error: isError,
      typing: isTyping,
      agentType,
      analysisId,
      timestamp: new Date()
    }]);
  };

  const addUserMessage = (content) => {
    setMessages(prev => [...prev, {
      role: 'user',
      content,
      timestamp: new Date()
    }]);
  };

  const updateLastAssistantMessage = (content) => {
    setMessages(prev => {
      const newMessages = [...prev];
      for (let i = newMessages.length - 1; i >= 0; i--) {
        if (newMessages[i].role === 'assistant') {
          newMessages[i] = { ...newMessages[i], content };
          break;
        }
      }
      return newMessages;
    });
  };

  const removeTypingMessages = () => {
    setMessages(prev => prev.filter(m => !m.typing));
  };

  /**
   * ============================================
   * AI CHAT FUNCTIONALITY (EXISTING)
   * ============================================
   */

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userInput = input;
    addUserMessage(userInput);
    setInput('');
    setLoading(true);

    const intent = detectIntent(userInput);

    // Handle recall intent locally (no backend call needed)
    if (intent.type === 'recall') {
      addAssistantMessage('...', false, true);
      await handleRecall(userInput);
      removeTypingMessages();
      setLoading(false);
      return;
    }

    // Show typing indicator
    addAssistantMessage('...', false, true);

    try {
      // If an agent is active, route all queries through quick-advice
      // with the agent_id so the backend uses the right system prompt
      const useAgentRouting = activeAgent && ['tactical', 'formation', 'movement', 'training', 'injury'].includes(activeAgent);

      let payload = { question: userInput };
      let endpoint = intent.endpoint;

      if (useAgentRouting) {
        // Agent mode — send everything through /api/agents/quick-advice with agent_id
        payload = { question: userInput, agent_id: activeAgent };
        endpoint = '/api/agents/quick-advice';
      } else {
        // Legacy intent-based routing (no agent selected)
        if (intent.type === 'formation-compare') {
          const forms = userInput.match(/\d-\d-\d(-\d)?/g);
          if (forms && forms.length === 2) {
            payload = { formation1: forms[0], formation2: forms[1] };
          } else {
            throw new Error('Please specify two formations to compare (e.g. "compare 4-3-3 vs 4-2-3-1")');
          }
        }
        if (intent.type === 'formation') {
          const singleForm = userInput.match(/\d-\d-\d(-\d)?/);
          if (singleForm) {
            payload = { formation: singleForm[0] };
          }
        }
        if (intent.type === 'training-drill') {
          payload = { objective: userInput.replace(/drill|training|for/gi, '').trim() || 'defensive positioning' };
        }
        if (intent.type === 'training') {
          const teamMatch = userInput.match(/AL\s+\w+|Al\s+\w+/i);
          payload = {
            opponentTeam: teamMatch ? teamMatch[0] : 'Unknown Team',
            daysBeforeMatch: 7,
            tacticalAnalysis: userInput
          };
        }
      }

      const response = await axios.post(`${CLOUD_RUN_API}${endpoint}`, payload, {
        timeout: 180000
      });

      removeTypingMessages();
      addAssistantMessage(
        parseAgentResponse(intent.type, response.data),
        false,
        false,
        intent.type
      );

    } catch (error) {
      console.error('Chat error:', error);
      removeTypingMessages();
      addAssistantMessage(
        `❌ ${error.response?.data?.detail || error.message || 'Analysis error. Please try again.'}`,
        true,
        false,
        intent.type
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const parseAgentResponse = (type, data) => {
    if (data.success) {
      if (data.response) return data.response;
      if (data.advice) return data.advice;
      if (data.explanation) return data.explanation;
      if (data.comparison) return data.comparison;
      if (data.drill) return data.drill;
      if (data.trainingPlan?.fullPlan) return data.trainingPlan.fullPlan;
      if (data.results?.training?.fullPlan) return data.results.training.fullPlan;
      return 'Analysis complete!';
    }
    if (data.error) return `Error: ${data.error}`;
    return 'I encountered an issue analyzing that. Please try rephrasing your question.';
  };

  /**
   * ============================================
   * ACTION HANDLERS (EXISTING)
   * ============================================
   */

  const handleSaveAnalysis = async (message) => {
    try {
      setLoading(true);
      
      const teamMatch = message.content.match(/AL\s+\w+|Al\s+\w+/i);
      const teamName = teamMatch ? teamMatch[0] : 'Unknown Team';
      
      const savedAnalyses = JSON.parse(localStorage.getItem('tahleel_analyses') || '[]');
      
      const newAnalysis = {
        id: `analysis_${Date.now()}`,
        teamName,
        content: message.content,
        agentType: message.agentType || 'tactical',
        timestamp: new Date().toISOString(),
        favorite: false,
        matchDate: new Date().toISOString().split('T')[0]
      };
      
      savedAnalyses.unshift(newAnalysis);
      if (savedAnalyses.length > 50) savedAnalyses.pop();
      localStorage.setItem('tahleel_analyses', JSON.stringify(savedAnalyses));
      
      addAssistantMessage(
        `✅ Analysis saved!\n\nTeam: ${teamName}\nID: ${newAnalysis.id}\n\nView in History tab.`
      );
    } catch (error) {
      addAssistantMessage(`❌ Error saving: ${error.message}`, true);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTraining = async (message) => {
    try {
      setLoading(true);

      const scenarioLabel = selectedScenario
        ? TRAINING_SCENARIOS.find(s => s.value === selectedScenario)?.label || selectedScenario
        : null;

      addAssistantMessage(
        scenarioLabel
          ? `Generating training plan for scenario: **${scenarioLabel}**...`
          : 'Generating 7-day training plan...',
        false, true
      );

      const teamMatch = message.content.match(/AL\s+\w+|Al\s+\w+/i);
      const teamName = teamMatch ? teamMatch[0] : 'the opponent';

      const requestBody = {
        opponentTeam: teamName,
        tacticalAnalysis: message.content,
        matchDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        daysBeforeMatch: 7,
      };

      if (selectedScenario) {
        requestBody.scenario = selectedScenario;
      }

      const response = await axios.post(`${CLOUD_RUN_API}/api/agents/training`, requestBody, { timeout: 180000 });

      const trainingContent = response.data.response || response.data.trainingPlan?.fullPlan || 'Training plan generated!';

      // Prepend scenario context if applicable
      const scenarioHeader = response.data.scenario
        ? `**Match Scenario: ${response.data.scenario.label}**\n\n`
        : '';

      removeTypingMessages();
      addAssistantMessage(scenarioHeader + trainingContent, false, false, 'training');

    } catch (error) {
      console.error('Training error:', error);
      removeTypingMessages();
      addAssistantMessage(`❌ Error generating training: ${error.message}`, true);
    } finally {
      setLoading(false);
    }
  };

  const handleShareAnalysis = (message) => {
    navigator.clipboard.writeText(message.content).then(() => {
      addAssistantMessage('📋 Copied to clipboard! Share with your team.');
    }).catch(() => {
      addAssistantMessage('❌ Unable to copy. Please select and copy manually.', true);
    });
  };

  /**
   * ============================================
   * UI HELPERS
   * ============================================
   */

  const getAgentIcon = (agentType) => {
    const icons = {
      'tactical': Target,
      'formation': Users,
      'formation-compare': Users,
      'training': BookOpen,
      'training-drill': BookOpen,
      'player-movement': TrendingUp,
      'full-analysis': Sparkles,
      'video-analysis': Video
    };
    return icons[agentType] || Bot;
  };

  /**
   * Simple markdown: **bold**, *italic*, `code`, --- to <hr>
   */
  const formatMessageContent = (text) => {
    if (!text) return '';
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;border-radius:8px;margin:8px 0;border:1px solid #e5e7eb" />')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code style="background:#f1f5f9;padding:1px 5px;border-radius:4px;font-size:0.85em">$1</code>')
      .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0">')
      .replace(/\n/g, '<br>');
    return html;
  };

  /**
   * ============================================
   * RENDER
   * ============================================
   */

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: '#0a1628' }}>
      {/* HEADER */}
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #1e3a5f' }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#f59e0b' }}>
              <Bot className="h-5 w-5" style={{ color: '#0a1628' }} />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full" style={{ background: '#22c55e', border: '2px solid #0a1628' }}></div>
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
              TAHLEEL<span style={{ color: '#f59e0b' }}>.ai</span>
            </h3>
            <p className="text-xs" style={{ color: '#64748b' }}>AI-Powered Sports Tactics</p>
          </div>
        </div>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingVideo || analyzingTeam}
          className="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all"
          style={{ background: '#f59e0b', color: '#0a1628' }}>
          {uploadingVideo ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /><span>{uploadProgress}%</span></>
          ) : (
            <><Video className="h-3.5 w-3.5" /><span>Upload Video</span></>
          )}
        </button>

        <input ref={fileInputRef} type="file" accept="video/*" onChange={handleVideoUpload} style={{ display: 'none' }} />
      </div>

      {/* MESSAGES */}
      <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 350px)', background: '#0a1628' }}>
        <div className="space-y-6">

          {/* Empty state — show when only welcome message exists */}
          {messages.length <= 1 && !uploadingVideo && !pendingJerseySelection && !pendingPreflightConfirm && (
            <div className="mt-8 max-w-lg mx-auto">
              <div className="text-center mb-8">
                <img src="/logo.svg" alt="" className="w-12 h-12 mx-auto mb-4 opacity-40" />
                <h3 className="text-base font-bold" style={{ color: '#ffffff' }}>How can I help you today?</h3>
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>Upload a match video or ask a tactical question</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: '📹', title: 'Upload Match Video', desc: 'Get a full tactical report in 5 minutes', action: () => fileInputRef.current?.click() },
                  { icon: '📐', title: 'Compare Formations', desc: 'e.g. "Compare 4-3-3 vs 4-4-2"', action: () => { setInput('Compare 4-3-3 vs 4-4-2'); inputRef.current?.focus(); } },
                  { icon: '🎯', title: 'Counter Tactics', desc: 'e.g. "How to beat a 4-2-3-1?"', action: () => { setInput('How to beat a 4-2-3-1 formation?'); inputRef.current?.focus(); } },
                  { icon: '🏋️', title: 'Training Plan', desc: 'e.g. "7-day training plan"', action: () => { setInput('Generate a 7-day training plan'); inputRef.current?.focus(); } },
                  { icon: '📊', title: 'Recall Analysis', desc: 'e.g. "Show my last analysis"', action: () => { setInput('Show my last analysis'); inputRef.current?.focus(); } },
                  { icon: '⚽', title: 'Quick Advice', desc: 'Ask any tactical question', action: () => { inputRef.current?.focus(); } },
                ].map((item, i) => (
                  <button key={i} onClick={item.action}
                    className="text-left p-3.5 rounded-xl transition-all group"
                    style={{ background: '#1e3a5f10', border: '1px solid #1e3a5f' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.background = 'rgba(245,158,11,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.background = '#1e3a5f10'; }}>
                    <span className="text-lg">{item.icon}</span>
                    <p className="text-xs font-semibold mt-1.5" style={{ color: '#f1f5f9' }}>{item.title}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#64748b' }}>{item.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message, index) => {
            const AgentIcon = message.agentType ? getAgentIcon(message.agentType) : null;
            
            return (
              <div key={index}>
                <div className={`flex items-start space-x-3 ${
                  message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-br from-royal-700 to-royal-900' 
                      : 'bg-gradient-gold'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="h-5 w-5 text-white" />
                    ) : AgentIcon ? (
                      <AgentIcon className="h-5 w-5 text-royal-900" />
                    ) : (
                      <Bot className="h-5 w-5 text-royal-900" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className={`flex-1 max-w-3xl ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <div className="inline-block px-5 py-3 rounded-2xl"
                      style={
                        message.role === 'user'
                          ? { background: '#f59e0b', color: '#0a1628' }
                          : message.error
                          ? { background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }
                          : { background: '#0f2040', color: '#f1f5f9', border: '1px solid #1e3a5f' }
                      }>
                      {message.typing ? (
                        <div className="flex space-x-2">
                          <div className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#f59e0b' }}></div>
                          <div className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#f59e0b', animationDelay: '0.2s' }}></div>
                          <div className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#f59e0b', animationDelay: '0.4s' }}></div>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap font-medium leading-relaxed"
                           dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }} />
                      )}
                    </div>

                    {/* Action Buttons for Analysis Results */}
                    {message.role === 'assistant' && !message.typing && !message.error && message.agentType && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(message.agentType === 'tactical' || message.agentType === 'video-analysis') && (
                          <>
                            <button
                              onClick={() => handleSaveAnalysis(message)}
                              disabled={loading}
                              className="px-3 py-1.5 text-xs bg-royal-600 hover:bg-royal-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-all"
                            >
                              💾 Save
                            </button>
                            <button
                              onClick={() => handleGenerateTraining(message)}
                              disabled={loading}
                              className="px-3 py-1.5 text-xs bg-gold-500 hover:bg-gold-600 disabled:bg-gray-400 text-royal-900 rounded-lg font-semibold transition-all"
                            >
                              📋 Generate Training
                            </button>
                            <button
                              onClick={() => handleShareAnalysis(message)}
                              disabled={loading}
                              className="px-3 py-1.5 text-xs bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-all"
                            >
                              📤 Share
                            </button>
                          </>
                        )}
                        {message.agentType === 'training' && (
                          <>
                            <button
                              onClick={() => handleShareAnalysis(message)}
                              className="px-3 py-1.5 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-all"
                            >
                              📤 Share
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-1 font-medium">
                      {message.timestamp.toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ============================================
              PREFLIGHT CONFIRMATION CARD
              ============================================ */}
          {pendingPreflightConfirm && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-blue-600">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 max-w-3xl">
                <div className="bg-white border-2 border-blue-200 rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Match Confirmation Required</span>
                  </div>

                  {/* Match details */}
                  <div className="space-y-2 mb-4">
                    {pendingPreflightConfirm.matchInfo.team0 && pendingPreflightConfirm.matchInfo.team1 && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-lg">&#9917;</span>
                        <span className="font-bold text-gray-900">
                          {pendingPreflightConfirm.matchInfo.team0} vs {pendingPreflightConfirm.matchInfo.team1}
                        </span>
                      </div>
                    )}
                    {pendingPreflightConfirm.matchInfo.jersey0 && (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                        <span className="text-gray-600">Team A Jersey</span>
                        <span className="font-semibold text-gray-900">{pendingPreflightConfirm.matchInfo.jersey0}</span>
                      </div>
                    )}
                    {pendingPreflightConfirm.matchInfo.jersey1 && (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                        <span className="text-gray-600">Team B Jersey</span>
                        <span className="font-semibold text-gray-900">{pendingPreflightConfirm.matchInfo.jersey1}</span>
                      </div>
                    )}
                    {pendingPreflightConfirm.matchInfo.confidence && (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                        <span className="text-gray-600">Confidence</span>
                        <span className={`font-semibold ${
                          pendingPreflightConfirm.matchInfo.confidence === 'high' ? 'text-green-600' :
                          pendingPreflightConfirm.matchInfo.confidence === 'medium' ? 'text-amber-600' : 'text-gray-500'
                        }`}>
                          {pendingPreflightConfirm.matchInfo.confidence.toUpperCase()}
                        </span>
                      </div>
                    )}

                    {/* Frame Analysis Stats */}
                    {pendingPreflightConfirm.matchInfo.frameAnalysis && pendingPreflightConfirm.matchInfo.frameAnalysis.total_sampled > 0 && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm">🗑️</span>
                          <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">AI Frame Quality Scan</span>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Sampled: </span>
                            <span className="font-bold text-gray-900">{pendingPreflightConfirm.matchInfo.frameAnalysis.total_sampled}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Usable: </span>
                            <span className="font-bold text-green-600">{pendingPreflightConfirm.matchInfo.frameAnalysis.tactical_frames}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Garbage: </span>
                            <span className="font-bold text-red-600">{pendingPreflightConfirm.matchInfo.frameAnalysis.garbage_frames}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Waste: </span>
                            <span className="font-bold text-amber-600">{pendingPreflightConfirm.matchInfo.frameAnalysis.garbage_rate_pct}%</span>
                          </div>
                        </div>
                        {pendingPreflightConfirm.matchInfo.frameAnalysis.garbage_summary && (
                          <p className="text-xs text-amber-700 mt-2 italic">{pendingPreflightConfirm.matchInfo.frameAnalysis.garbage_summary}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handlePreflightConfirm(true)}
                      className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-sm"
                    >
                      Yes, correct match
                    </button>
                    <button
                      onClick={() => handlePreflightConfirm(false)}
                      className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-all"
                    >
                      No, wrong match
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Auto-continuing in 2 minutes if no response...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ============================================
              INLINE JERSEY SELECTOR
              ============================================ */}
          {pendingJerseySelection && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-gradient-gold">
                <Shirt className="h-5 w-5 text-royal-900" />
              </div>
              
              <div className="flex-1 max-w-3xl">
                <div className="bg-white border-2 border-gold-300 rounded-2xl shadow-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Select Team to Analyze
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {pendingJerseySelection.jerseys.map((jersey) => (
                      <JerseyCard
                        key={jersey.team_id}
                        jersey={jersey}
                        onClick={() => handleTeamSelect(jersey)}
                        disabled={analyzingTeam}
                      />
                    ))}
                  </div>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Click on a jersey to analyze that team's tactics
                  </p>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* INPUT AREA */}
      <div className="p-4" style={{ borderTop: '1px solid #1e3a5f', background: '#0a1628' }}>

        {/* Scenario Selector — visible when training agent is active */}
        {(activeAgent === 'training') && (
          <div className="mb-3 flex items-center gap-2">
            <label className="text-xs font-semibold whitespace-nowrap" style={{ color: '#94a3b8' }}>
              Match Scenario:
            </label>
            <select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer"
              style={{
                background: '#0f2040',
                border: '1px solid #1e3a5f',
                color: selectedScenario ? '#f59e0b' : '#94a3b8',
                outline: 'none',
                maxWidth: '400px',
              }}
            >
              {TRAINING_SCENARIOS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {selectedScenario && (
              <button
                onClick={() => setSelectedScenario('')}
                className="px-2 py-1 rounded text-xs font-semibold transition-all"
                style={{ background: '#1e3a5f', color: '#94a3b8' }}
              >
                Clear
              </button>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about tactics, formations, or training..."
            disabled={loading || uploadingVideo || analyzingTeam}
            className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
            style={{ background: '#0f2040', border: '1px solid #1e3a5f', color: '#f1f5f9', outline: 'none' }}
            onFocus={e => { e.target.style.borderColor = '#f59e0b'; e.target.style.boxShadow = '0 0 0 2px rgba(245,158,11,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = '#1e3a5f'; e.target.style.boxShadow = 'none'; }}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim() || uploadingVideo || analyzingTeam}
            className="px-5 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 text-sm"
            style={{
              background: (loading || !input.trim()) ? '#1e3a5f' : '#f59e0b',
              color: (loading || !input.trim()) ? '#64748b' : '#0a1628',
              cursor: (loading || !input.trim()) ? 'not-allowed' : 'pointer'
            }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="hidden sm:inline">Send</span>
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {[
            { icon: '🎯', label: 'Analyze Team', prompt: 'Analyze opponent weaknesses' },
            { icon: '⚽', label: 'Compare Formations', prompt: 'Compare 4-3-3 vs 4-2-3-1' },
            { icon: '📋', label: 'Generate Training', prompt: 'Create a 7-day training plan' },
            { icon: '🎥', label: 'Upload Video', action: 'upload' },
          ].map((btn, i) => (
            <button key={i}
              onClick={() => btn.action === 'upload' ? fileInputRef.current?.click() : setInput(btn.prompt)}
              disabled={loading || uploadingVideo}
              className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all disabled:opacity-30"
              style={{ background: btn.action === 'upload' ? '#f59e0b' : '#0f2040', color: btn.action === 'upload' ? '#0a1628' : '#94a3b8', border: `1px solid ${btn.action === 'upload' ? '#f59e0b' : '#1e3a5f'}` }}>
              {btn.icon} {btn.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * JerseyCard Component - Inline jersey selection
 */
const JerseyCard = ({ jersey, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
        disabled
          ? 'opacity-50 cursor-not-allowed border-gray-200'
          : 'border-gray-200 hover:border-gold-500 hover:shadow-lg cursor-pointer'
      }`}
    >
      {/* Jersey Color Preview */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-12 h-14 rounded-lg shadow-inner border-2 border-gray-200"
          style={{
            backgroundColor: jersey.hex,
            backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(0,0,0,0.1) 100%)`
          }}
        />
        
        <div>
          <h4 className="font-bold text-gray-900">
            {jersey.color_name}
          </h4>
          <p className="text-xs text-gray-500">
            {jersey.hex}
          </p>
        </div>
      </div>

      {/* Player Count */}
      <div className="flex items-center gap-1 text-sm text-gray-600">
        <Users className="h-4 w-4" />
        <span><strong>{jersey.player_count}</strong> players</span>
      </div>

      {/* Click to Analyze */}
      <div className="mt-3 text-center py-2 rounded-lg bg-gold-100 text-gold-800 text-xs font-semibold">
        Click to Analyze
      </div>
    </button>
  );
};

export default ChatInterface;
